import app from 'flarum/forum/app';
import Application from 'flarum/common/Application';
import { extend, override } from 'flarum/common/extend';
import UserControls from 'flarum/forum/utils/UserControls';
import ComposerState from 'flarum/forum/states/ComposerState';
import Button from 'flarum/common/components/Button';
import { extendUser, remainingSeconds, formatRemaining } from '../common/breakState';
import BreakModal from './components/BreakModal';

const ERROR_CODE = 'interaction_break';
const CONTROLS = 'happydigital-interaction-break.forum.controls';

function showBreakModal(minutes, remaining) {
  app.modal.show(BreakModal, { minutes, remaining });
}

/** Show the notice instead of the composer. Returns true when blocked. */
function blockedByBreak() {
  const user = app.session.user;
  const remaining = remainingSeconds(user);

  if (remaining <= 0) return false;

  showBreakModal(user.interactionBreakMinutes(), remaining);

  return true;
}

function sendBreakRequest(user, method, body) {
  return app
    .request({
      method,
      url: `${app.forum.attribute('apiUrl')}/users/${user.id()}/interaction-break`,
      body,
    })
    .then((payload) => {
      app.store.pushPayload(payload);
      m.redraw();
    })
    // Errors (e.g. "already active") are already alerted by Flarum's handler.
    .catch(() => {});
}

app.initializers.add('happydigital-interaction-break', () => {
  extendUser();

  // ─── Moderator controls (shown to groups holding user.giveInteractionBreak) ─
  extend(UserControls, 'moderationControls', (items, user) => {
    if (!user.canGiveInteractionBreak()) return;

    const remaining = remainingSeconds(user);

    if (remaining > 0) {
      items.add(
        'interactionBreakStatus',
        <Button icon="fas fa-hourglass-half" disabled>
          {app.translator.trans(`${CONTROLS}.remaining`, { time: formatRemaining(remaining) })}
        </Button>,
        -10
      );
      items.add(
        'interactionBreakEnd',
        <Button icon="fas fa-play" onclick={() => sendBreakRequest(user, 'DELETE')}>
          {app.translator.trans(`${CONTROLS}.end`)}
        </Button>,
        -11
      );
      return;
    }

    [5, 60].forEach((minutes, i) => {
      items.add(
        `interactionBreak${minutes}`,
        <Button icon="fas fa-hourglass-half" onclick={() => sendBreakRequest(user, 'POST', { minutes })}>
          {app.translator.trans(`${CONTROLS}.give_${minutes}`)}
        </Button>,
        -10 - i
      );
    });
  });

  // ─── Composer gate: replace the composer with the notice during a break ───
  override(ComposerState.prototype, 'load', function (original, componentClass, attrs) {
    if (blockedByBreak()) return;

    return original(componentClass, attrs);
  });

  // load() was skipped, so there is nothing to show; don't open an empty composer.
  override(ComposerState.prototype, 'show', function (original) {
    if (!this.body || !this.body.componentClass) return;

    return original();
  });

  // ─── Server-side rejection (e.g. a composer that was already open) ────────
  override(Application.prototype, 'requestErrorDefaultHandler', function (original, e, isDebug, formattedErrors) {
    const error = e && e.response && e.response.errors && e.response.errors[0];

    if (error && error.code === ERROR_CODE) {
      showBreakModal(error.minutes, error.remaining);
      return;
    }

    return original(e, isDebug, formattedErrors);
  });
});
